// Catalog.ID Client — AES-256-GCM Encryption
// ============================================================================
// Uses the native WebCrypto API — no external dependencies.

/** Generate a random 32-byte AES-256 key as raw bytes. */
export function generateSymmetricKey(): Uint8Array {
    return crypto.getRandomValues(new Uint8Array(32));
}

/** Generate a random 12-byte nonce for AES-GCM. */
export function generateNonce(): Uint8Array {
    return crypto.getRandomValues(new Uint8Array(12));
}

/** Import raw key bytes into a CryptoKey for AES-GCM. */
async function importKey(rawKey: Uint8Array): Promise<CryptoKey> {
    return crypto.subtle.importKey(
        'raw',
        rawKey as unknown as ArrayBuffer,
        { name: 'AES-GCM' },
        false,
        ['encrypt', 'decrypt'],
    );
}

/** Encrypt plaintext with AES-256-GCM. Returns ciphertext (includes auth tag). */
export async function encrypt(key: Uint8Array, nonce: Uint8Array, plaintext: Uint8Array): Promise<Uint8Array> {
    const cryptoKey = await importKey(key);
    const ct = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv: nonce as unknown as ArrayBuffer },
        cryptoKey,
        plaintext as unknown as ArrayBuffer,
    );
    return new Uint8Array(ct);
}

/** Decrypt AES-256-GCM ciphertext. */
export async function decrypt(key: Uint8Array, nonce: Uint8Array, ciphertext: Uint8Array): Promise<Uint8Array> {
    const cryptoKey = await importKey(key);
    const pt = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: nonce as unknown as ArrayBuffer },
        cryptoKey,
        ciphertext as unknown as ArrayBuffer,
    );
    return new Uint8Array(pt);
}

/** Compute SHA-256 hash of data. Returns 32 bytes. */
export async function sha256(data: Uint8Array): Promise<Uint8Array> {
    const hash = await crypto.subtle.digest('SHA-256', data as unknown as ArrayBuffer);
    return new Uint8Array(hash);
}

/** Compute SHA-512 hash of data. Returns 64 bytes. */
export async function sha512(data: Uint8Array): Promise<Uint8Array> {
    const hash = await crypto.subtle.digest('SHA-512', data as unknown as ArrayBuffer);
    return new Uint8Array(hash);
}

/** Encode bytes to lowercase hex. */
export function toHex(bytes: Uint8Array): string {
    let hex = '';
    for (const b of bytes) hex += b.toString(16).padStart(2, '0');
    return hex;
}

/** Encode bytes to base64. */
export function toBase64(bytes: Uint8Array): string {
    let binary = '';
    for (const b of bytes) binary += String.fromCharCode(b);
    return btoa(binary);
}

/** Decode base64 to bytes. */
export function fromBase64(b64: string): Uint8Array {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
}

/** Encode string to UTF-8 bytes. */
export function encodeUtf8(text: string): Uint8Array {
    return new TextEncoder().encode(text);
}

/** Decode UTF-8 bytes to string. */
export function decodeUtf8(bytes: Uint8Array): string {
    return new TextDecoder().decode(bytes);
}
